import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, HStack, Pressable, Text, VStack,
} from '@gluestack-ui/themed';

type ToolGroupActivity = {
  activityType: 'tool_call' | 'tool_result';
  operation: string;
  summary?: string;
  error?: string;
};

export type ToolGroupCardProps = {
  activities: ToolGroupActivity[];
};

const OPERATION_LABELS: Record<string, string> = {
  'modules.list': 'Listed modules',
  'modules.get': 'Read module',
  'modules.resolve_working_version': 'Checked version',
  'modules.create_version': 'Created version',
  'modules.delete_version': 'Deleted version',
  'modules.update': 'Updated module',
  'tasks.list': 'Listed tasks',
  'tasks.get': 'Read task',
  'tasks.create': 'Created task',
  'tasks.update': 'Updated task',
  'tasks.delete': 'Deleted task',
  'resources.list': 'Listed resources',
  'resources.get': 'Read resource',
  'resources.create_link': 'Added resource',
  'resources.update': 'Updated resource',
  'resources.delete': 'Deleted resource',
  'platform.get_guide': 'Read guide',
  'platform.whoami': 'Checked identity',
};

const toLabel = (op: string) => OPERATION_LABELS[op] ?? op;

const UUID_PATTERN = /[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi;

const stripUuids = (text: string) => text.replace(UUID_PATTERN, '…');

const buildCollapsedLabels = (activities: ToolGroupActivity[]): string => {
  // Count by tool invocation only (tool_call), not results, to avoid doubling
  const calls = activities.filter((a) => a.activityType === 'tool_call');
  const counts = new Map<string, number>();
  calls.forEach((a) => counts.set(a.operation, (counts.get(a.operation) || 0) + 1));
  const seen = new Set<string>();
  const parts: string[] = [];
  calls.forEach((a) => {
    if (seen.has(a.operation)) return;
    seen.add(a.operation);
    const count = counts.get(a.operation) || 1;
    const label = toLabel(a.operation);
    parts.push(count > 1 ? `${label} (×${count})` : label);
  });
  return parts.join(' · ');
};

/**
 * ToolGroupCard
 * ----
 * Collapsible inline row grouping all tool activity from one assistant turn.
 */
const ToolGroupCard: React.FC<ToolGroupCardProps> = ({ activities }) => {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const hasError = activities.some((activity) => !!activity.error);
  const collapsedLabels = useMemo(() => buildCollapsedLabels(activities), [activities]);

  return (
    <Box testID="assistant-tool-group">
      <Pressable
        onPress={() => setExpanded((open) => !open)}
        testID="assistant-tool-group-toggle"
      >
        <HStack alignItems="center" space="xs">
          {!!hasError && (
            <Text size="xs" color="$statusError">•</Text>
          )}
          <Text size="xs" color="$textSubtle">
            {`› ${collapsedLabels}`}
          </Text>
        </HStack>
      </Pressable>
      {!!expanded && (
        <VStack space="xs" pl="$3" pt="$1">
          {activities
            .filter((a) => a.activityType === 'tool_call')
            .map((call) => {
              const result = activities.find(
                (a) => a.activityType === 'tool_result' && a.operation === call.operation,
              );
              return (
                <Box key={`call-${call.operation}-${call.summary || ''}`}>
                  <HStack alignItems="center" space="xs">
                    <Text size="xs" color="$textSubtle" fontWeight="semibold">
                      {toLabel(call.operation)}
                    </Text>
                    <Text size="xs" color="$textHint">
                      {call.operation}
                    </Text>
                  </HStack>
                  {!!result?.summary && (
                    <Text size="xs" color="$textBody">{stripUuids(result.summary)}</Text>
                  )}
                  {!!result?.error && (
                    <Text size="xs" color="$statusError">{stripUuids(result.error)}</Text>
                  )}
                </Box>
              );
            })}
          <Text size="xs" color="$textHint" fontStyle="italic">
            {t('admin.assistant.activity.toolGroup')}
          </Text>
        </VStack>
      )}
    </Box>
  );
};

export default ToolGroupCard;
